import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Upload, Mic, Waves, AudioLines } from "lucide-react";

const TRANSCRIPTION_STAGES = [
  { label: "Uploading audio...", icon: Upload, duration: 1400 },
  { label: "Transcribing speech...", icon: Waves, duration: 3200 },
  { label: "Analyzing voice...", icon: AudioLines, duration: 2400 },
];

const TRAINING_STAGES = [
  { label: "Reading your samples...", icon: Mic, duration: 1600 },
  { label: "Mapping voice patterns...", icon: Waves, duration: 2800 },
  { label: "Building your voice model...", icon: AudioLines, duration: 2200 },
];

interface ProgressProps {
  isActive: boolean;
  variant?: "transcription" | "training";
  capturedDuration?: string | null;
  onCancel?: () => void;
}

export function TranscriptionProgress({ isActive, variant = "transcription", capturedDuration, onCancel }: ProgressProps) {
  const [stageIndex, setStageIndex] = useState(0);
  const [progress, setProgress] = useState(0);

  const stages = variant === "transcription" ? TRANSCRIPTION_STAGES : TRAINING_STAGES;

  useEffect(() => {
    if (!isActive) {
      setStageIndex(0);
      setProgress(0);
      return;
    }

    setStageIndex(0);
    setProgress(0);

    let currentStage = 0;
    const timers: ReturnType<typeof setTimeout>[] = [];

    const advanceStage = () => {
      if (currentStage < stages.length - 1) {
        currentStage++;
        setStageIndex(currentStage);
      }
    };

    let elapsed = 0;
    for (let i = 0; i < stages.length - 1; i++) {
      elapsed += stages[i].duration;
      timers.push(setTimeout(advanceStage, elapsed));
    }

    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 88) return prev;
        const increment = prev < 25 ? 2.8 : prev < 55 ? 1.6 : prev < 75 ? 0.9 : 0.35;
        return Math.min(prev + increment, 88);
      });
    }, 200);

    return () => {
      timers.forEach(clearTimeout);
      clearInterval(progressInterval);
    };
  }, [isActive, variant]);

  if (!isActive) return null;

  const stage = stages[stageIndex];
  const StageIcon = stage.icon;

  return (
    <div className="flex flex-col items-center justify-center gap-5 py-6" role="status" aria-live="polite" aria-busy="true">
      <div className="relative w-16 h-16">
        <motion.div
          className="absolute inset-0 rounded-2xl bg-primary/10"
          animate={{ scale: [1, 1.15, 1], opacity: [0.5, 0.2, 0.5] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        />
        <div className="absolute inset-0 rounded-2xl bg-primary/5 flex items-center justify-center">
          <AnimatePresence mode="wait">
            <motion.div
              key={stageIndex}
              initial={{ opacity: 0, scale: 0.8, y: 5 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: -5 }}
              transition={{ duration: 0.3 }}
            >
              <StageIcon className="w-7 h-7 text-primary" />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      <div className="w-full max-w-xs space-y-3">
        {capturedDuration && (
          <p className="text-xs text-muted-foreground text-center">{capturedDuration}</p>
        )}
        <div
          className="h-2 bg-secondary/50 rounded-full overflow-hidden"
          role="progressbar"
          aria-valuenow={Math.round(progress)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${variant === "transcription" ? "Transcription" : "Voice training"} progress`}
        >
          <motion.div
            className="h-full rounded-full"
            style={{ background: "linear-gradient(90deg, hsl(188 90% 42%), hsl(205 88% 52%))" }}
            initial={{ width: "0%" }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          />
        </div>

        <AnimatePresence mode="wait">
          <motion.p
            key={stageIndex}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            className="text-sm font-medium text-muted-foreground text-center"
          >
            {stage.label}
          </motion.p>
        </AnimatePresence>
      </div>

      {onCancel && (
        <button
          onClick={onCancel}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors underline underline-offset-2"
        >
          Cancel
        </button>
      )}
    </div>
  );
}
